import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import RequirePermission, { RESEARCH_DOCS_RESOURCE, RESEARCH_DOCS_ACTION_READ } from 'utils/route-guard/RequirePermission';

//workspace
const WorkspaceDocument = Loadable(lazy(() => import('views/workspace/document')));
const WorkspaceStorage = Loadable(lazy(() => import('views/workspace/storage')));
const WorkspaceStorageRouter = Loadable(lazy(() => import('views/workspace/storage/WorkspaceStorageRouter')));
const WorkspaceChatGPT = Loadable(lazy(() => import('views/workspace/chatgpt')));
const WorkspaceMindmap = Loadable(lazy(() => import('views/workspace/mindmap')));
const WorkspaceTranslation = Loadable(lazy(() => import('views/workspace/translation')));
const WorkspacePromptEngineerEducation = Loadable(lazy(() => import('views/workspace/prompt_engineer/Education')));
const WorkspacePromptEngineerForeignLanguage = Loadable(lazy(() => import('views/workspace/prompt_engineer/ForeignLanguage')));
const WorkspacePromptEngineerMarketing = Loadable(lazy(() => import('views/workspace/prompt_engineer/Marketing')));
const WorkspacePromptEngineerBusiness = Loadable(lazy(() => import('views/workspace/prompt_engineer/Business')));
const WorkspacePromptEngineerManage = Loadable(lazy(() => import('views/workspace/prompt_engineer/Manage')));
const WorkspacePromptEngineerFinance = Loadable(lazy(() => import('views/workspace/prompt_engineer/Finance')));
// datasource routing
const DatasourceOverview = Loadable(lazy(() => import('views/datasource/Overview')));
const DatasourceTables = Loadable(lazy(() => import('views/datasource/Tables')));
const DatasourceProcesses = Loadable(lazy(() => import('views/datasource/Processes')));

const DatasourceTablesSvb24Product = Loadable(lazy(() => import('views/datasource/Tables/svb24')));
const DatasourceTablesSvb24Image = Loadable(lazy(() => import('views/datasource/Tables/svb24_images')));

const DatasourceTablesAmazonProduct = Loadable(lazy(() => import('views/datasource/Tables/amazon')));
const DatasourceTablesAmazonImage = Loadable(lazy(() => import('views/datasource/Tables/amazon_images')));

const DatasourceTablesWestmarineProduct = Loadable(lazy(() => import('views/datasource/Tables/westmarine')));
const DatasourceTablesWestmarineImage = Loadable(lazy(() => import('views/datasource/Tables/westmarine_images')));

const DatasourceTablesAlibabaProduct = Loadable(lazy(() => import('views/datasource/Tables/alibaba')));
const DatasourceTablesAlibabaImage = Loadable(lazy(() => import('views/datasource/Tables/alibaba_images')));

const AppUserAccountProfile3 = Loadable(lazy(() => import('views/application/users/account-profile/Profile3')));
const AppProfileCardStyle1 = Loadable(lazy(() => import('views/application/users/card/CardStyle1')));

const AppProjectManagement = Loadable(lazy(() => import('views/application/customer')));

// DataHub Dashboard
const DataHubDashboard = Loadable(lazy(() => import('views/dashboard/DataHubDashboard')));

// Project Management
const ProjectManagement = Loadable(lazy(() => import('views/projects/ProjectManagement')));

// Storage Management
const StorageManagement = Loadable(lazy(() => import('views/storage/StorageManagement')));
const StorageDashboard = Loadable(lazy(() => import('views/storage/StorageDashboard')));

// Database Management
const DatabaseDashboard = Loadable(lazy(() => import('views/database/DatabaseDashboard')));

// Jupyter Hub (legacy components restored)
const JupyterDashboard = Loadable(lazy(() => import('views/jupyter/JupyterDashboard')));

// V2 Components (disabled - rollback)
// const JupyterDashboardV2 = Loadable(lazy(() => import('views/jupyter/JupyterDashboardV2')));
// const EnvironmentManagerV2 = Loadable(lazy(() => import('views/jupyter/EnvironmentManagerV2')));

// Workflow Management
const WorkflowDashboard = Loadable(lazy(() => import('views/workflow/WorkflowDashboard')));

// API Gateway
const ApiGatewayDashboard = Loadable(lazy(() => import('views/api-gateway/ApiGatewayDashboard')));

// Model Serving
const ModelServingDashboard = Loadable(lazy(() => import('views/model-serving/ModelServingDashboard')));

// Lambda Functions
const LambdaDashboard = Loadable(lazy(() => import('views/lambda-functions/LambdaDashboard')));
const FunctionEditor = Loadable(lazy(() => import('views/lambda-functions/FunctionEditor')));
const FunctionMetrics = Loadable(lazy(() => import('views/lambda-functions/FunctionMetrics')));
const FunctionsList = Loadable(lazy(() => import('views/lambda-functions/FunctionsList')));
const LambdaSettings = Loadable(lazy(() => import('views/lambda-functions/LambdaSettings')));

// DataHub Routes
import DataHubRoutes from './DataHubRoutes';

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: '/workspace/chatgpt',
            element: <WorkspaceChatGPT />
        },
        {
            path: '/workspace/storage',
            element: <WorkspaceStorage />
        },
        // Workspace Storage Module Routes
        {
            path: '/workspace/storage/:projectId/lakehouse',
            element: <WorkspaceStorageRouter module="lakehouse" />
        },
        {
            path: '/workspace/storage/:projectId/cv',
            element: <WorkspaceStorageRouter module="cv" />
        },
        {
            path: '/workspace/storage/:projectId/mongodb',
            element: <WorkspaceStorageRouter module="mongodb" />
        },
        {
            path: '/workspace/storage/:projectId/airflow',
            element: <WorkspaceStorageRouter module="airflow" />
        },
        {
            path: '/workspace/storage/:projectId/api-gateway',
            element: <WorkspaceStorageRouter module="api-gateway" />
        },
        {
            path: '/workspace/document',
            element: (
                <RequirePermission resource={RESEARCH_DOCS_RESOURCE} action={RESEARCH_DOCS_ACTION_READ}>
                    <WorkspaceDocument />
                </RequirePermission>
            )
        },
        {
            path: '/workspace/mindmap',
            element: <WorkspaceMindmap />
        },
        {
            path: '/workspace/translation',
            element: <WorkspaceTranslation />
        },
        {
            path: '/workspace/prompt_engineer/education',
            element: <WorkspacePromptEngineerEducation />
        },
        {
            path: '/workspace/prompt_engineer/foreign_language',
            element: <WorkspacePromptEngineerForeignLanguage />
        },
        {
            path: '/workspace/prompt_engineer/marketing',
            element: <WorkspacePromptEngineerMarketing />
        },
        {
            path: '/workspace/prompt_engineer/business',
            element: <WorkspacePromptEngineerBusiness />
        },
        {
            path: '/workspace/prompt_engineer/manage',
            element: <WorkspacePromptEngineerManage />
        },
        {
            path: '/workspace/prompt_engineer/finance',
            element: <WorkspacePromptEngineerFinance />
        },
        {
            path: '/user/account-profile/profile3',
            element: <AppUserAccountProfile3 />
        },
        // {
        //     path: '/user/card/card1',
        //     element: <AppProfileCardStyle1 />
        // },
        {
            path: '/datasource/processes',
            element: <DatasourceProcesses />
        },
        {
            path: '/datasource/tables/svb24/products',
            element: <DatasourceTablesSvb24Product />
        },
        {
            path: '/datasource/tables/svb24/images',
            element: <DatasourceTablesSvb24Image />
        },
        {
            path: '/datasource/tables/amazon/products',
            element: <DatasourceTablesAmazonProduct />
        },
        {
            path: '/datasource/tables/amazon/images',
            element: <DatasourceTablesAmazonImage />
        },
        {
            path: '/datasource/tables/westmarine/products',
            element: <DatasourceTablesWestmarineProduct />
        },
        {
            path: '/datasource/tables/westmarine/images',
            element: <DatasourceTablesWestmarineImage />
        },
        {
            path: '/datasource/tables/alibaba/products',
            element: <DatasourceTablesAlibabaProduct />
        },
        {
            path: '/datasource/tables/alibaba/images',
            element: <DatasourceTablesAlibabaImage />
        },
        {
            path: '/datasource/overview',
            element: <DatasourceOverview />
        },
        {
            path: '/datasource/tables',
            element: <DatasourceTables />
        },
        {
            path: '/storage/dashboard',
            element: <StorageDashboard />
        },
        {
            path: '/database/dashboard',
            element: <DatabaseDashboard />
        },
        {
            path: '/jupyter/dashboard',
            element: <JupyterDashboard />
        },
        {
            path: '/workflow/dashboard',
            element: <WorkflowDashboard />
        },
        {
            path: '/api-gateway/dashboard',
            element: <ApiGatewayDashboard />
        },
        {
            path: '/model-serving/dashboard',
            element: <ModelServingDashboard />
        },
        {
            path: '/customer',
            element: <AppProjectManagement />
        },
        {
            path: '/lambda/dashboard',
            element: <LambdaDashboard />
        },
        {
            path: '/lambda/functions/:functionId/edit',
            element: <FunctionEditor />
        },
        {
            path: '/lambda/metrics/:functionId',
            element: <FunctionMetrics />
        },
        {
            path: '/lambda/functions',
            element: <FunctionsList />
        },
        {
            path: '/lambda/settings',
            element: <LambdaSettings />
        },
        // Storage routes
        {
            path: '/storage/files',
            element: <StorageDashboard />
        },
        {
            path: '/storage/upload',
            element: <StorageDashboard />
        },
        {
            path: '/storage/cloud',
            element: <StorageDashboard />
        },
        {
            path: '/storage/analytics',
            element: <StorageDashboard />
        },
        {
            path: '/storage/settings',
            element: <StorageDashboard />
        },
        // Database routes
        {
            path: '/database/collections',
            element: <DatabaseDashboard />
        },
        {
            path: '/database/query',
            element: <DatabaseDashboard />
        },
        {
            path: '/database/analytics',
            element: <DatabaseDashboard />
        },
        {
            path: '/database/connections',
            element: <DatabaseDashboard />
        },
        {
            path: '/database/settings',
            element: <DatabaseDashboard />
        },
        // Jupyter routes (using legacy components)
        {
            path: '/jupyter/notebooks',
            element: <JupyterDashboard />
        },
        {
            path: '/jupyter/environments',
            element: <JupyterDashboard />
        },
        {
            path: '/jupyter/environments/:userId',
            element: <JupyterDashboard />
        },
        {
            path: '/jupyter/servers',
            element: <JupyterDashboard />
        },
        {
            path: '/jupyter/analytics',
            element: <JupyterDashboard />
        },
        {
            path: '/jupyter/settings',
            element: <JupyterDashboard />
        },
        // Legacy Jupyter routes (for backwards compatibility)
        {
            path: '/jupyter/legacy/dashboard',
            element: <JupyterDashboard />
        },
        // Workflow routes
        {
            path: '/workflow/dags',
            element: <WorkflowDashboard />
        },
        {
            path: '/workflow/runs',
            element: <WorkflowDashboard />
        },
        {
            path: '/workflow/scheduler',
            element: <WorkflowDashboard />
        },
        {
            path: '/workflow/analytics',
            element: <WorkflowDashboard />
        },
        {
            path: '/workflow/servers',
            element: <WorkflowDashboard />
        },
        // API Gateway routes
        {
            path: '/api-gateway/api-keys',
            element: <ApiGatewayDashboard />
        },
        {
            path: '/api-gateway/webhooks',
            element: <ApiGatewayDashboard />
        },
        {
            path: '/api-gateway/rate-limiting',
            element: <ApiGatewayDashboard />
        },
        {
            path: '/api-gateway/usage',
            element: <ApiGatewayDashboard />
        },
        {
            path: '/api-gateway/monitoring',
            element: <ApiGatewayDashboard />
        },
        // Lambda routes
        {
            path: '/lambda/deploy',
            element: <LambdaDashboard />
        },
        {
            path: '/lambda/metrics',
            element: <FunctionMetrics />
        },
        {
            path: '/lambda/logs',
            element: <LambdaDashboard />
        },
        {
            path: '/lambda/test',
            element: <LambdaDashboard />
        },
        // Model Serving routes
        {
            path: '/model-serving/deploy',
            element: <ModelServingDashboard />
        },
        {
            path: '/model-serving/endpoints',
            element: <ModelServingDashboard />
        },
        {
            path: '/model-serving/templates',
            element: <ModelServingDashboard />
        },
        {
            path: '/model-serving/monitoring',
            element: <ModelServingDashboard />
        },
        // DataHub Routes
        ...DataHubRoutes
    ]
};

export default MainRoutes;
